import React from 'react'
import get from 'lodash/get'
import find from 'lodash/find'
import pick from 'lodash/pick'
import { SearchOutlined } from '@ant-design/icons'

import ColumnSearch from './ColumnSearch'
import { ColumnsModel } from './DataTable'
import { DateTime } from 'luxon'
import { removeMultipleSpaces } from '@utils/string'

interface BuildColumnArgument {
  column: ColumnsModel
  errors: any
  form: any
  onUpdate: any
  page: number
  pageSize: number
}

export const buildColumn = ({
  column,
  errors,
  form,
  onUpdate,
  page,
  pageSize
}: BuildColumnArgument) => {
  const filterOptions = columnFilter(column)
  return {
    /**
     * Merge default global column props with provided columns
     */
    key: column.dataIndex,
    sorter: column.sortable
      ? (a, b) => {
          const valueA = get(a, column.dataIndex!)
          const valueB = get(b, column.dataIndex!)

          if (column.isDate) {
            const dateA = DateTime.fromISO(valueA)
            const dateB = DateTime.fromISO(valueB)

            if (dateA.isValid && dateB.isValid) {
              return dateA < dateB ? -1 : dateA > dateB ? 1 : 0
            }
          }

          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        }
      : false,
    onCell: (row, pageIndex) => ({
      row,
      form,
      column,
      disabled:
        typeof column.disabled === 'function'
          ? column.disabled(row)
          : !!column.disabled,
      errorMessage: get(
        find(errors, error => {
          // Gets the index relative to all data and not just the index on the page
          const index = (page - 1) * pageSize + pageIndex

          return error.path[0] === index && error.path[1] === column.dataIndex
        }),
        'message'
      ),
      onUpdate
    }),
    ...filterOptions,
    ...column
  }
}

export const getActionColumn = (actions): ColumnsModel => ({
  title: 'Actions',
  dataIndex: 'actions',
  sortable: false,
  align: 'right',
  render: (text, record) => {
    return (
      <div className="table-actions">
        {actions && actions.map((action, i) => action(record))}
      </div>
    )
  }
})

export const columnFilter = (column: ColumnsModel) => {
  if (!column.filterable) {
    return {}
  }

  return {
    filterDropdown: options => (
      <ColumnSearch column={column} filterOptions={options} />
    ),

    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),

    onFilter: (searchTerm: string, record: object): boolean => {
      const recordValue: unknown = get(record, column.dataIndex!)
      const searchTermTrimmed = removeMultipleSpaces(searchTerm).toLowerCase()

      return filterValue(searchTermTrimmed, recordValue, column.filterKeys)
    }
  }
}

// This returns true/false if it can find the searchTerm in the recordValue
const filterValue = (
  searchTerm: string,
  recordValue: unknown,
  filterKeys?: string[]
): boolean => {
  if (recordValue === null || recordValue === undefined) {
    return false
  }

  if (recordValue instanceof Array) {
    return recordValue.some(item => {
      if (typeof item === 'object' && !(item instanceof Array)) {
        return searchObjectValues(item, searchTerm, filterKeys)
      }

      return searchBasicTypes(item, searchTerm)
    })
  }

  // If an object (not an array), search the object's values
  if (typeof recordValue === 'object') {
    return searchObjectValues(recordValue!, searchTerm, filterKeys)
  }

  // At this point, recordValue is neither null, undefined, an array, or an object
  return searchBasicTypes(recordValue, searchTerm)
}

const searchObjectValues = (
  obj: object,
  searchTerm: string,
  filterKeys?: string[]
) => {
  // If we have filterKeys, search on only the keys specified
  const objToSearch = filterKeys ? pick(obj, filterKeys) : obj
  const objValues = Object.values(objToSearch)

  return objValues.some(value => searchBasicTypes(value, searchTerm))
}

const searchBasicTypes = (value: unknown, searchTerm: string) => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    const valueString = removeMultipleSpaces(String(value)).toLowerCase()

    return valueString.includes(searchTerm)
  }

  return false
}
